import { capitalize } from "lodash"

import { formatNumber } from "~/utils/formatting"
import type { ColliViolation, MoqViolation, ViolationIndex } from "~/utils/quantityViolations"

/**
 * Tooltip copy for the shared MOQ/colli rules (SUP-412), used by the Fashion and NOOS buy tables
 * and the PO page. A single SKU gets its figures; a group row gets a count, since listing every SKU
 * of a product does not fit a tooltip.
 */

export function formatGroupVariableName(variable: string): string {
  if (variable === "variantId") return "variant"
  if (variable === "productId") return "product"
  return variable
}

/** `default` is the NOOS buy plan and PO page; `plannedBuy` the Fashion buy plan's wording. */
export type ViolationWording = "default" | "plannedBuy"

export type ViolationMessageOptions = {
  isGroup: boolean
  /** Default wording: the group variable's name ("variant"). Planned-buy: the secondary MOQ's label. */
  groupName: string
  /** Limit colli to one warehouse; all, prefixed with the warehouse name, when omitted. */
  warehouseId?: string
  warehouseNames?: ReadonlyMap<string, string>
  wording?: ViolationWording
}

export type ViolationMessages = { moq: string[]; colli: string[]; suggestedActions: string[] }

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

export function skuMoqMessage(v: MoqViolation, wording: ViolationWording = "default"): string {
  if (wording === "plannedBuy") {
    return `The planned buy across warehouses (${formatNumber(v.amount, 0)}) is below the MOQ (${formatNumber(v.moq, 0)}). Short by ${formatNumber(v.moq - v.amount, 0)}.`
  }
  return `Minimum order not met: ${v.amount} units ordered across warehouses, minimum is ${v.moq} units.`
}

export function groupMoqMessage(
  v: MoqViolation,
  groupName: string,
  wording: ViolationWording = "default",
): string {
  if (wording === "plannedBuy") {
    return `The group's planned buy across warehouses (${formatNumber(v.amount, 0)}) is below the ${groupName} (${formatNumber(v.moq, 0)}). Short by ${formatNumber(v.moq - v.amount, 0)}.`
  }
  return `${capitalize(groupName)} minimum not met: ${v.amount} units total, minimum is ${v.moq} units.`
}

export function skuMoqLines(
  index: ViolationIndex,
  skuIds: readonly string[],
  { isGroup, wording = "default" }: Pick<ViolationMessageOptions, "isGroup" | "wording">,
): string[] {
  const short = filterShort(index, skuIds)
  if (short.length === 0) return []
  if (!isGroup && short.length === 1) return [skuMoqMessage(short[0]!, wording)]
  if (wording === "plannedBuy") {
    if (!isGroup) return short.map((v) => skuMoqMessage(v, wording))
    return [`${plural(short.length, "size is", "sizes are")} below their MOQ.`]
  }
  return [
    `${plural(short.length, "SKU has a quantity", "SKUs have quantities")} below the minimum order quantity.`,
  ]
}

function filterShort(index: ViolationIndex, skuIds: readonly string[]): MoqViolation[] {
  const out: MoqViolation[] = []
  for (const skuId of skuIds) {
    const v = index.skuMoq.get(skuId)
    if (v) out.push(v)
  }
  return out
}

function groupMoqLines(
  index: ViolationIndex,
  skuIds: readonly string[],
  { isGroup, groupName, wording = "default" }: ViolationMessageOptions,
): string[] {
  // The planned-buy table states the group shortfall on the variant row only.
  if (wording === "plannedBuy" && !isGroup) return []
  const lines: string[] = []
  const seen = new Set<string>()
  for (const skuId of skuIds) {
    const groupKey = index.groupKeyBySku.get(skuId)
    if (groupKey === undefined || seen.has(groupKey)) continue
    seen.add(groupKey)
    const v = index.secondaryMoq.get(groupKey)
    if (v) lines.push(groupMoqMessage(v, groupName, wording))
  }
  return lines
}

function colliMessage(
  v: ColliViolation,
  warehouseName: string | undefined,
  wording: ViolationWording,
): string {
  const where = warehouseName ? `${warehouseName}: ` : ""
  if (wording === "plannedBuy") {
    return `${where}${formatNumber(v.amount, 0)} is not a multiple of the colli size (${formatNumber(v.colliSize, 0)}).`
  }
  const rounded = Math.ceil(v.amount / v.colliSize) * v.colliSize
  return `${where}Quantity must be a multiple of ${v.colliSize} units (colli size). ${v.amount} → ${rounded} units.`
}

function colliCountMessage(count: number, perWarehouse: boolean, wording: ViolationWording) {
  if (wording === "default") {
    return `${plural(count, "quantity is", "quantities are")} not a multiple of the colli size.`
  }
  if (perWarehouse) {
    return `${plural(count, "size is", "sizes are")} not a multiple of the colli size here.`
  }
  return `${plural(count, "warehouse quantity is", "warehouse quantities are")} not a multiple of the colli size.`
}

export function colliLines(
  index: ViolationIndex,
  skuIds: readonly string[],
  {
    isGroup,
    warehouseId,
    warehouseNames,
    wording = "default",
  }: Omit<ViolationMessageOptions, "groupName">,
): string[] {
  const hits: (ColliViolation & { warehouseId: string })[] = []
  for (const skuId of skuIds) {
    for (const [whId, v] of index.colliBySku.get(skuId) ?? []) {
      if (warehouseId === undefined || whId === warehouseId) hits.push({ warehouseId: whId, ...v })
    }
  }
  if (hits.length === 0) return []
  if (isGroup) return [colliCountMessage(hits.length, warehouseId !== undefined, wording)]
  return hits.map((hit) => {
    if (warehouseId !== undefined) return colliMessage(hit, undefined, wording)
    const name = warehouseNames?.get(hit.warehouseId)
    return colliMessage(hit, wording === "default" ? (name ?? hit.warehouseId) : name, wording)
  })
}

/** MOQ, colli and suggested-action lines for a set of SKUs; a group row passes all its leaves. */
export function violationMessages(
  index: ViolationIndex,
  skuIds: readonly string[],
  options: ViolationMessageOptions,
): ViolationMessages {
  const moq = [...skuMoqLines(index, skuIds, options), ...groupMoqLines(index, skuIds, options)]
  const colli = colliLines(index, skuIds, options)
  const suggestedActions: string[] = []
  if (moq.length > 0) suggestedActions.push("Use 'Resolve MOQ violations' to raise quantities.")
  if (colli.length > 0) suggestedActions.push("Use 'Resolve colli size violations' to round up.")
  return { moq, colli, suggestedActions }
}

export function partiallySelectedGroupsMessage(count: number, groupName: string): string {
  return `${plural(count, `${groupName} has`, `${groupName}s have`)} only some SKUs selected. MOQ violations on partly selected ${groupName}s are not resolved. Select the whole ${groupName} to resolve it.`
}
